import { Credit } from './Credit';
import { RevenueDataProps } from '@pages/credit-risk/analysis/components/RevenueTable';
import { TaxCropDataProps } from 'src/pages/credit-risk/analysis/components/TaxCropTable';
import { AddemVariablesProps } from '@pages/credit-risk/analysis/components/AddemVariablesTable';
import { DropdownInfoDataProps } from '@pages/credit-risk/analysis/components/DropdownInfoWidget';
import { TotalVariablesProps } from '@pages/credit-risk/analysis/components/TotalVariablesTable';
import { CreditBureauReport, CreditBureauData } from '@pages/credit-risk/analysis/components/CreditBureau';
import { ScoreTableData } from '@pages/credit-risk/analysis/components/ScoreTable';
import { RiskQuestionType } from './Risk';
import {
  CreditReportTypes,
  TableBureauHeader, TableBureauScoreHeader,
  TableCreditBureauRow,
  TableCreditBureauScoreRow, TableCropHeader, TableCropRow, TableFiscalHeader, TableFiscalRow,
  TablePastCycleRow,
  TableProjectedCycleRow,
} from '@network';

export interface CreditReport {
  id: number,
  type: CreditReportTypes,
  creditId: number,
  adminId: number,
  fileId: number,
  file: File | null,
}

export interface IFinancialTableRow {
  key: string;
  title: string;
  value: number;
  percent?: number;
}

export type FinancialResType = 'VERIFIABLE' | 'NON_VERIFIABLE';

export enum FinancialResult {
  VERIFIABLE = 'VERIFIABLE',
  NON_VERIFIABLE = 'NON_VERIFIABLE',
}

export interface IFinancialResOption {
  label: string;
  key: FinancialResType;
}

export interface IFinancialTable {
  title: string;
  key: string;
  result: {options: IFinancialResOption[]; selected: FinancialResType};
  values: IFinancialTableRow[];
}

export interface RiskGeography {
  category: 'A' | 'B' | 'C' | 'D';
  grade: number;
  name: string;
}

export interface ReplyAnswer {
  id: number;
  progressPercent: number | null;
  title: string;
}

export interface ReplyCategory {
  id: number;
  position: number;
  title: string;
}

export interface ReplyQuestion {
  answerId: number | null;
  answers: ReplyAnswer[];
  category: ReplyCategory | null;
  division: string;
  questionId: number;
  title: string;
  titleMx: string;
  type: RiskQuestionType;
  isFixed: boolean;
  tag: string;
  value: string | null;
  position: number;
  progressPercent: number | null;
  isRequiredForContract?: boolean;
}

export interface CreditRiskProgressOptions {
  [key: string]: boolean;
}

export interface CreditRiskProgressProps {
  percent: number;
  options: CreditRiskProgressOptions;
}

export interface CreditRisk {
  addemVariables: RiskValues;
  addemVariablesTable: AddemVariablesProps;
  approvedAmount: number;
  associateNames: string | null;
  buyer: string;
  buyerNames: string | null;
  completedAnalysisAt: string | null;
  createdAt: string;
  credit: Credit;
  creditBureau: CreditBureauReport;
  creditBureauOld: CreditBureauData;
  creditId: number;
  creditRejectReason: string | null;
  creditRejectReasonId: number | null;
  creditTerm: string;
  cropTechnification: RiskInfo;
  financialPastTable: IFinancialTable;
  financialProjectedTable: IFinancialTable;
  financialRatiosNetProfit: number | null,
  financialRatiosStockholdersEquity: number | null,
  financialRatiosTotalLiabilities: number | null,
  financialRatiosTotalAssets: number | null,
  financialRatiosDebtToAssetRatio: number | null,
  financialRatiosDebtToEquityRatio: number | null,
  financialRatiosROE: number | null,
  financialRatiosCashRatio: number | null,
  financialRatiosDscr: number | null,
  creditBureauAnualRevenue: number | null,
  geography: RiskGeography;
  id: number;
  incomeSuppliers: {
    avgDpo: number;
    values: {
      period: number;
      emitted: number;
    }[]
  };
  insurance: number;
  interestRate: number;
  isFixed: boolean;
  isLegacy: boolean;
  isOld: boolean;
  membership: number;
  moratoryRate: number | null;
  notaryFees: number;
  offTakerBusinessAge: DropdownInfoDataProps,
  offTakerLegalIncidences: DropdownInfoDataProps,
  offTakerLegalTimeWorking: DropdownInfoDataProps,
  offTakerPaymentsDso: DropdownInfoDataProps,
  otherVariables: RiskValues;
  otherVariablesTable: any;
  pagaComission: number;
  productionType: ProductionType | null;
  replyOther: ReplyQuestion[];
  replyScore: ReplyQuestion[];
  replyTerms: ReplyQuestion[];
  retailerNames: string | null;
  riskProgress: CreditRiskProgressProps;
  riskReplies: ReplyQuestion[];
  riskScore: number | null;
  riskStatus: CreditRiskStatus;
  scoreAmount: DropdownInfoDataProps;
  scoreCashRatio: ScoreTableData;
  scoreTerm: RiskInfo;
  showAddemVariables: boolean;
  specialConditions: string;
  state: CreditRiskState;
  successPercent: number;
  supplier: '' | null;
  supplierDpo: DropdownInfoDataProps,
  supplierLegalIncidences: DropdownInfoDataProps,
  supplierScore: DropdownInfoDataProps,
  supplierTimeWorking: DropdownInfoDataProps,
  tablePastCycle: {rows: TablePastCycleRow[]};
  tableProjectedCycle: {rows: TableProjectedCycleRow[]};
  tableFiscal: {headers: TableFiscalHeader[], rows: TableFiscalRow[]};
  tableCrop: {headers: TableCropHeader[], rows: TableCropRow[]};
  tableCreditBureau: {headers: TableBureauHeader[], rows: TableCreditBureauRow[]};
  tableCreditBureauScore: {headers: TableBureauScoreHeader[], rows: TableCreditBureauScoreRow[]};
  financialStatement?: string;
  taxCrop: TaxCropDataProps;
  taxExpenditures: RevenueDataProps;
  technologyType: TechnologyType | null;
  totalVariables: TotalVariablesProps;
  totalVariablesTable: TotalVariablesProps;
  updatedAt: string;
  variety: string | null;
}

export interface RiskValues {
  values: {
    key: string;
    title: string;
    value: string
  }[];
}

export interface RiskInfo {
  selected: string;
  value: number;
  extra: number;
}

export enum CreditRiskState {
  PENDING = 'pending',
  COMPLETE = 'complete',
}

export enum CreditRiskStatus {
  DENIED = 'denied',
  APPROVED = 'approved',
}

export enum TechnologyType {
  OPEN_FIELD = 'open_field',
  GREENHOUSE = 'greenhouse',
  MACRO_TUNNEL = 'macro_tunnel',
  SHADE_NET = 'shade_net',
}

export const technologyTypeOptions = [
  { id: TechnologyType.OPEN_FIELD, name: 'Open Field' },
  { id: TechnologyType.GREENHOUSE, name: 'Greenhouse' },
  { id: TechnologyType.MACRO_TUNNEL, name: 'Macro Tunnel' },
  { id: TechnologyType.SHADE_NET, name: 'Shade Net' },
];

export enum ProductionType {
  CONVENTIONAL = 'conventional',
  ORGANIC = 'organic',
}

export const productionTypeOptions = [
  { id: ProductionType.CONVENTIONAL, name: 'Conventional' },
  { id: ProductionType.ORGANIC, name: 'Organic' },
];

export const fixedRiskText = 'This record can\'t be edited because the related credit has statuses Signed, Delay, Restructure, Denied or this risk analysis record has the "Complete" status of "Underwritting" value.';
